import * as React from "react";
import { RouteComponentProps } from "react-router";
import { PartnerAPI } from "src/infra/api/service/partner_api";
import Assets from "src/presentation/assets/img/assets";
import styled from "styled-components";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import { FrontDate } from "../../domain/model/frontDate";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";

const apiClient: IApiClient = createApiClient();
const partnerAPI = new PartnerAPI(apiClient);

interface IProps extends RouteComponentProps<{}> {}

const CustomerPartnerStoreList: React.FC<IProps> = (props) => {
  const [partners, setPartners] = React.useState<entity.IPartner[]>([]);
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);

  React.useEffect(() => {
    try {
      partnerAPI.getAll().then((items) => {
        setPartners(items);
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }

    const searchParams = new URLSearchParams(props.location.search);
    const successParam = searchParams.get("success");
    if (successParam === "true") {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.INFO,
          "パートナーストアの申し込みが完了しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  const groupBy = <K extends PropertyKey, V>(
    array: readonly V[],
    getKey: (cur: V, idx: number, src: readonly V[]) => K
  ) =>
    array.reduce((obj, cur, idx, src) => {
      const key = getKey(cur, idx, src);
      (obj[key] || (obj[key] = []))!.push(cur);
      return obj;
    }, {} as Partial<Record<K, V[]>>);

  const categories = partners.map((partner) => partner.category!);
  const uniqueCategories = Array.from(
    new Map(categories.map((category) => [category.id, category])).values()
  );
  const partnersByCategory = groupBy(
    partners,
    (partner) => partner.category!.id!
  );

  const partnerContent = (partner: entity.IPartner) => {
    return (
      <>
        <Partner>
          <p
            style={{
              fontSize: 14,
              fontWeight: "bold",
              color: "#333",
              marginBottom: 16,
            }}
          >
            {partner.companyName}
          </p>

          <PartnerContent>
            <PartnerContentLeft>
              <img style={{ width: 172 }} src={partner.signedThumbnailURL!} />
              <p style={{ fontSize: 12, marginTop: 16 }}>
                {partner.serviceIntroduction}
              </p>
            </PartnerContentLeft>

            <PartnerContentRight>
              <p>🎁 特典</p>
              <p
                style={{
                  width: 120,
                  marginTop: 10,
                  fontSize: 12,
                  marginBottom: 20,
                  paddingLeft: 20,
                }}
              >
                {partner.companyIncentive}
              </p>

              <p>💰 インセンティブ</p>
              <p
                style={{
                  width: 120,
                  marginTop: 10,
                  fontSize: 12,
                  marginBottom: 20,
                  paddingLeft: 20,
                }}
              >
                {partner.customerIncentive}
              </p>
            </PartnerContentRight>
          </PartnerContent>

          <div style={{ flex: 1 }} />

          <PartnerButtons>
            <Button>
              <a
                style={{ color: "white" }}
                target="_blank"
                href={partner.signedServiceDocumentURL!}
              >
                紹介資料
              </a>
            </Button>
            <Button>
              <a
                style={{ color: "white" }}
                href={`/customer/partner-store-form/${partner.id}`}
              >
                紹介する
              </a>
            </Button>
          </PartnerButtons>
        </Partner>
      </>
    );
  };

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}

      <PageWrapper>
        <Top>
          <img
            style={{ width: 70, height: 150, marginRight: 20 }}
            src={Assets.partnerStoreListIc1}
          />
          <TopText>
            <p
              style={{
                fontSize: 16,
                fontWeight: "bold",
                color: "#333",
                lineHeight: "18px",
                marginBottom: 10,
              }}
            >
              エキスパートネットワークの
            </p>
            <p
              style={{
                fontSize: 30,
                fontWeight: "bold",
                color: "#333",
              }}
            >
              パートナーストアの紹介プロダクトリスト
            </p>
          </TopText>
        </Top>

        <a
          style={{
            fontSize: 12,
            fontWeight: "bold",
            color: "#9A8C64",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          href="/customer/partner-store"
        >
          <span
            style={{
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #9A8C64",
              marginRight: 4,
            }}
          ></span>
          パートナーストアの使い方をみる
        </a>

        <Flow>
          <div style={{ width: 160 }}>
            <FlowImg>
              <img style={{ width: 125 }} src={Assets.partnerStoreListIc2} />
            </FlowImg>
            <FlowText>紹介したいプロダクトの紹介ボタンをクリック</FlowText>
          </div>

          <img style={{ width: 30 }} src={Assets.partnerStoreListArrow} />

          <div style={{ width: 140 }}>
            <FlowImg>
              <img style={{ width: 125 }} src={Assets.partnerStoreListIc3} />
            </FlowImg>
            <FlowText>紹介先企業の情報等をフォームに入力</FlowText>
          </div>

          <img style={{ width: 30 }} src={Assets.partnerStoreListArrow} />

          <div style={{ width: 140 }}>
            <FlowImg>
              <img style={{ height: 80 }} src={Assets.partnerStoreListIc4} />
            </FlowImg>
            <FlowText>パートナーが紹介先企業にコンタクト</FlowText>
          </div>

          <img style={{ width: 30 }} src={Assets.partnerStoreListArrow} />

          <div style={{ width: 140 }}>
            <FlowImg>
              <img style={{ width: 60 }} src={Assets.partnerStoreListIc5} />
            </FlowImg>
            <FlowText>そのプロダクトを契約パートナーからLiberaへ報告</FlowText>
          </div>

          <img style={{ width: 30 }} src={Assets.partnerStoreListArrow} />

          <div style={{ width: 140 }}>
            <FlowImg>
              <img style={{ width: 80 }} src={Assets.partnerStoreListIc6} />
            </FlowImg>
            <FlowText>Liberaより皆様へ成約を報告紹介手数料を獲得</FlowText>
          </div>
        </Flow>

        {uniqueCategories.map((category) => (
          <>
            <p
              style={{
                fontWeight: "bold",
                fontSize: 20,
                color: "#333",
                lineHeight: "18px",
                marginTop: 40,
              }}
            >
              {category.name!!}
            </p>
            <PartnerList>
              {partnersByCategory[category.id!!]!!.map((partner) =>
                partnerContent(partner)
              )}
            </PartnerList>
          </>
        ))}
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerPartnerStoreList;

const Top = styled.div`
  display: flex;
  align-items: center;
  width: 960px;
  margin: 0 auto;
  margin-bottom: 20px;
`;

const TopText = styled.div`
  width: 800px;
`;

const Flow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 0 auto;
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
`;

const FlowImg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 125px;
  height: 80px;
  margin-bottom: 10px;
`;

const FlowText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 400;
`;

const PartnerList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 920px;
  margin: 0 auto;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Partner = styled.div`
  width: 450px;
  padding: 15px 30px;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const PartnerContent = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
`;

const PartnerContentLeft = styled.div`
  width: 240px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-right: 1px solid #d8d8d8;
  padding-right: 20px;
`;

const PartnerContentRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  padding-top: 10px;
`;

const PartnerButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const Button = styled.button`
  width: 184px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  background-color: #00a8b2;
  border-radius: 2px;
  margin: 0 auto;
  margin-top: 20px;
  border: none;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_partner_store_list.tsx